/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */


import {emptyQueryHistory, QueryHistoryEntry, QueryHistoryState} from "qqq/models/query/QueryHistory";

///////////////////////////////////////////////////////////////////////////////////
// query history lives in IndexedDB, not local storage: entries hold whole views //
// (every column, and filters that can carry thousands of values), which would   //
// fill local storage's small per-site quota - and that quota is shared with the //
// app's other local storage writes.  One record per user, table and table       //
// variant - see buildQueryHistoryKey.                                           //
///////////////////////////////////////////////////////////////////////////////////
export const QUERY_HISTORY_DATABASE_NAME = "qqq.queryHistory";
export const QUERY_HISTORY_STORE_NAME = "histories";

/////////////////////////////////////////////////////////////////////////////////
// some browsers (e.g., some Safari versions and private modes) can leave an   //
// open request that never succeeds or fails - give up on it after this long, //
// so history can't hang the work queued behind it.  A browser that does that //
// keeps doing it, so after one such timeout, history stays off for the page  //
// (rather than every read and write waiting out a timeout of its own).       //
/////////////////////////////////////////////////////////////////////////////////
export const QUERY_HISTORY_OPEN_TIMEOUT_MILLIS = 5000;

let databasePromise: Promise<IDBDatabase> | null = null;
let openTimedOut = false;


/*******************************************************************************
 ** the key a history is stored under.  Each user gets their own history (so the
 ** next person to log in on a shared browser can't see or restore it), as does
 ** each variant of a table, since a query restored under a different variant
 ** would run against different data.  A user without an id is "anonymous".
 *******************************************************************************/
export function buildQueryHistoryKey(userId: string | null | undefined, tableName: string, tableVariant: {type?: string, id?: any} | null | undefined): string
{
   const parts: any[] = [userId ?? "anonymous", tableName];
   if (tableVariant)
   {
      parts.push(tableVariant.type ?? null, tableVariant.id ?? null);
   }
   return (JSON.stringify(parts));
}


/*******************************************************************************
 ** open (once) the history database, creating its store the first time.  A
 ** failed open is not kept, so a later call tries again - and neither is a
 ** connection the browser closes (site data cleared) or that another tab needs
 ** closed (deleting the database, or opening a newer version of it).
 *******************************************************************************/
function openDatabase(): Promise<IDBDatabase>
{
   if (openTimedOut)
   {
      return (Promise.reject(new Error("Opening the query history database timed out earlier - not trying again")));
   }

   if (databasePromise == null)
   {
      const thisPromise = new Promise<IDBDatabase>((resolve, reject) =>
      {
         let timedOut = false;
         const timeout = setTimeout(() =>
         {
            timedOut = true;
            openTimedOut = true;
            reject(new Error("Opening the query history database timed out"));
         }, QUERY_HISTORY_OPEN_TIMEOUT_MILLIS);

         const request = indexedDB.open(QUERY_HISTORY_DATABASE_NAME, 1);
         request.onupgradeneeded = () => request.result.createObjectStore(QUERY_HISTORY_STORE_NAME);
         request.onsuccess = () =>
         {
            clearTimeout(timeout);
            const database = request.result;
            if (timedOut)
            {
               ////////////////////////////////////////////////////////////
               // given up on already - don't leave this connection open //
               ////////////////////////////////////////////////////////////
               database.close();
               return;
            }
            database.onversionchange = () =>
            {
               database.close();
               forgetDatabase(thisPromise);
            };
            database.onclose = () => forgetDatabase(thisPromise);
            resolve(database);
         };
         request.onerror = () =>
         {
            clearTimeout(timeout);
            reject(request.error);
         };
      });
      thisPromise.catch(() => forgetDatabase(thisPromise));
      databasePromise = thisPromise;
   }
   return (databasePromise);
}


/*******************************************************************************
 ** stop using a connection (if it is still the one in use), so the next call
 ** opens a new one.
 *******************************************************************************/
function forgetDatabase(promise: Promise<IDBDatabase>): void
{
   if (databasePromise === promise)
   {
      databasePromise = null;
   }
}


/*******************************************************************************
 ** true if the entry is safe for the history model to use - the model parses
 ** every entry's viewJson, so an entry that does not parse would break it.
 *******************************************************************************/
function isUsableEntry(entry: any): entry is QueryHistoryEntry
{
   if (entry == null || typeof entry !== "object" || typeof entry.id !== "string" || typeof entry.viewJson !== "string" || typeof entry.lastUsedAt !== "number")
   {
      return (false);
   }

   try
   {
      JSON.parse(entry.viewJson);
      return (true);
   }
   catch (e)
   {
      return (false);
   }
}


/*******************************************************************************
 ** turn whatever is stored for a table into a usable history.  Never throws -
 ** anything unrecognizable, or of an unknown version, comes back as an empty
 ** history, and malformed entries are dropped, so that history can never break
 ** the screen.
 *******************************************************************************/
export function sanitizeStoredQueryHistory(stored: any): QueryHistoryState
{
   if (stored == null || typeof stored !== "object" || stored.version !== 1 || !Array.isArray(stored.entries))
   {
      return (emptyQueryHistory());
   }

   const entries: QueryHistoryEntry[] = stored.entries.filter(isUsableEntry);
   const currentEntryId = entries.some((entry) => entry.id === stored.currentEntryId) ? stored.currentEntryId : null;
   return ({version: 1, currentEntryId: currentEntryId, entries: entries});
}


/*******************************************************************************
 ** read the history stored under a key (from buildQueryHistoryKey).  Never
 ** rejects - when the database can't be read, the history comes back empty.
 *******************************************************************************/
export async function readQueryHistory(historyKey: string): Promise<QueryHistoryState>
{
   const opening = openDatabase();
   try
   {
      const database = await opening;
      const stored = await new Promise<any>((resolve, reject) =>
      {
         const request = database.transaction(QUERY_HISTORY_STORE_NAME, "readonly").objectStore(QUERY_HISTORY_STORE_NAME).get(historyKey);
         request.onsuccess = () => resolve(request.result);
         request.onerror = () => reject(request.error);
      });
      return (sanitizeStoredQueryHistory(stored));
   }
   catch (e)
   {
      console.log("Error reading query history", e);
      forgetDatabaseIfClosed(opening, e);
      return (emptyQueryHistory());
   }
}


/*******************************************************************************
 ** in one transaction - so another tab can't write in between - read the history
 ** stored under a key, apply the updater, and write the result.  Resolves with what was
 ** written, once it is committed; rejects if it could not be written, in which
 ** case the stored history is unchanged.
 **
 ** An updater that returns the very state it was given changed nothing - then
 ** nothing is written, and that state is what resolves.
 *******************************************************************************/
export async function updateQueryHistory(historyKey: string, updater: (state: QueryHistoryState) => QueryHistoryState): Promise<QueryHistoryState>
{
   const opening = openDatabase();
   try
   {
      const database = await opening;
      return (await new Promise<QueryHistoryState>((resolve, reject) =>
      {
         let updated: QueryHistoryState = null;
         const transaction = database.transaction(QUERY_HISTORY_STORE_NAME, "readwrite");
         transaction.oncomplete = () => resolve(updated);
         transaction.onerror = () => reject(transaction.error);
         transaction.onabort = () => reject(transaction.error ?? new Error("Query history update was aborted"));

         const store = transaction.objectStore(QUERY_HISTORY_STORE_NAME);
         const getRequest = store.get(historyKey);
         getRequest.onsuccess = () =>
         {
            try
            {
               const stored = sanitizeStoredQueryHistory(getRequest.result);
               updated = updater(stored);
               if (updated !== stored)
               {
                  store.put(updated, historyKey);
               }
            }
            catch (e)
            {
               reject(e);
               transaction.abort();
            }
         };
      }));
   }
   catch (e)
   {
      forgetDatabaseIfClosed(opening, e);
      throw (e);
   }
}


/*******************************************************************************
 ** a connection that was closed under us (without a close event reaching us)
 ** fails every transaction with InvalidStateError - stop using it.  Other
 ** failures (a full disk, say) leave a working connection, so keep that.
 *******************************************************************************/
function forgetDatabaseIfClosed(promise: Promise<IDBDatabase>, error: any): void
{
   if (error?.name === "InvalidStateError")
   {
      forgetDatabase(promise);
   }
}
